import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { APP_ICONS } from '../../../icons/icon-provider';
import { NotificationMessageComponent } from '../notification-message/notification-message.component';
import { NotificationAvatar } from '../notification-message/notification-avatar';
import { JobService } from 'src/app/services/job.service';

/** Distance (px) from the bottom of the list at which we pre-fetch the next
 *  page. Small enough that the user doesn't wait; large enough to avoid
 *  thrashing on tiny wheel deltas. */
const LOAD_MORE_THRESHOLD_PX = 80;

export interface NotificationItem {
  id: string;
  /** Sender / subject name, as the /notifications page's left column shows it. */
  title: string;
  /** Same avatar the page shows: photo, initials, logo, or a fixed icon. */
  avatar: NotificationAvatar;
  time: string;
  unread: boolean;
  /** The pre-processed backend record; rendered by <app-notification-message>
   *  so the slider shows exactly the /notifications page copy. */
  raw: any;
}

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [CommonModule, NgIconComponent, NotificationMessageComponent],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss'],
})
export class NotificationPanelComponent {
  @Input() open = false;
  @Input() notifications: NotificationItem[] = [];
  @Input() unreadCount = 0;
  @Input() loading = false;
  /** True while appending the next page (infinite scroll indicator). */
  @Input() loadingMore = false;
  /** True when another page is available — controls whether scroll emits loadMore. */
  @Input() hasMore = false;
  /** Opt-in: only show "Mark all read" when the consumer has a backing API.
   *  Defaults off so the button doesn't appear until a bulk-read endpoint exists. */
  @Input() canMarkAllRead = false;
  /** True while notification/markAllAsRead is in flight. Drives the button's
   *  spinner and disabled state so the user can't fire the bulk write twice —
   *  the request walks every page of the user's notifications, so a double
   *  click is an expensive duplicate, not a harmless one. */
  @Input() markingAllRead = false;

  @Output() closed = new EventEmitter<void>();
  @Output() viewAll = new EventEmitter<void>();
  @Output() markAllRead = new EventEmitter<void>();
  @Output() notifClicked = new EventEmitter<NotificationItem>();
  @Output() loadMore = new EventEmitter<void>();

  constructor(private jobService: JobService) {}

  // Initials avatar: same letters and name-derived colours the page uses
  // (JobService.getInitialsparam / getColorparam / getTextColor).
  initials(n: NotificationItem): string {
    const a = n.avatar;
    return a.kind === 'initials' ? this.jobService.getInitialsparam(a.firstName, a.lastName) : '';
  }
  initialsBg(n: NotificationItem): string | null {
    const a = n.avatar;
    return a.kind === 'initials' ? this.jobService.getColorparam(a.firstName, a.lastName) : null;
  }
  initialsFg(n: NotificationItem): string | null {
    const a = n.avatar;
    return a.kind === 'initials' ? this.jobService.getTextColor(a.firstName, a.lastName) : null;
  }

  close() { if (this.open) this.closed.emit(); }
  onViewAll() { this.viewAll.emit(); }
  onMarkAllRead() {
    // Second line of defence behind [disabled] — a rapid double click can land
    // two events before change detection paints the disabled state.
    if (this.markingAllRead) return;
    this.markAllRead.emit();
  }
  onItemClick(item: NotificationItem) { this.notifClicked.emit(item); }

  /** Fires when the list is scrolled within `LOAD_MORE_THRESHOLD_PX` of the
   *  bottom. Emits `loadMore` so the consumer can fetch the next page. */
  onListScroll(ev: Event) {
    if (!this.hasMore || this.loadingMore || this.loading) return;
    const el = ev.target as HTMLElement;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom <= LOAD_MORE_THRESHOLD_PX) {
      this.loadMore.emit();
    }
  }

  @HostListener('document:keydown.escape') onEscape() { this.close(); }
}
